// READ-ONLY diagnostics: in-memory event ring buffer + plain-text debug report builder.
// Never records tokens, cookies, Authorization headers or request bodies.
import { getOccurrencesOnIso, overrideAudit, getMissedOccurrences, isOccurrenceSnoozed, todayStr } from "../utils";

// TEMPORARY completion tracing: the ring buffer is persisted to localStorage so a reload / PWA
// relaunch between "I completed it" and "it came back" does not lose the trail. Still no
// tokens, cookies, headers or bodies — only task ids, occurrence keys, completion keys and titles.
const MAX_EVENTS = 300;
const RING_KEY = "meyetasks_diag_ring";
const TRACED_KEY = "meyetasks_diag_traced";
const load = (k, fb) => { try { const v = JSON.parse(localStorage.getItem(k) || "null"); return Array.isArray(v) ? v : fb; } catch { return fb; } };
const events = typeof window !== "undefined" ? load(RING_KEY, []) : [];
const auth = { authenticated: null, source: "unknown", lastVerify: null, lastVerifyAt: null, last401At: null };
let persistTimer = null;
const persist = () => {
    if (typeof window === "undefined") return;
    clearTimeout(persistTimer);
    persistTimer = setTimeout(() => { try { localStorage.setItem(RING_KEY, JSON.stringify(events)); } catch { /* quota */ } }, 250);
};

export const logDiag = (evt) => {
    events.push({ ts: new Date().toISOString(), ...evt });
    if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
    persist();
};
export const getDiagEvents = () => [...events];
export const clearDiagEvents = () => { events.splice(0, events.length); persist(); };

/** Occurrences the user completed recently (task_id + occurrenceIso) — these are traced through
 *  SSE / fetch / render so a reappearance can be explained. Kept for 7 days, max 30. */
export const rememberTraced = (task_id, iso, title) => {
    if (typeof window === "undefined") return;
    const list = load(TRACED_KEY, []).filter((t) => Date.now() - t.ts < 7 * 86_400_000 && !(t.task_id === task_id && t.iso === iso));
    list.push({ task_id, iso: iso || null, title: String(title || "").slice(0, 60), ts: Date.now() });
    try { localStorage.setItem(TRACED_KEY, JSON.stringify(list.slice(-30))); } catch { /* quota */ }
};
export const getTraced = () => (typeof window === "undefined" ? [] : load(TRACED_KEY, []));
export const tracedIds = () => new Set(getTraced().map((t) => t.task_id));
/** Compact recurrence-state fingerprint for one task (what every trace event records). */
export const stateSummary = (t) => t ? ({
    rev: t.rev ?? null, completed: !!t.completed, completed_at: t.completed_at ?? null,
    completions_n: (t.completions || []).length, completions_tail: (t.completions || []).slice(-4),
    snoozes: Object.keys(t.occurrence_snoozes || {}), overrides: (t.instance_overrides || []).map((o) => `${o.from}>${o.to}`),
}) : { missing: true };
export const setAuthDiag = (patch) => Object.assign(auth, patch);
export const getAuthDiag = () => ({ ...auth });

const safeUrl = (u = "") => String(u).replace(/[?&](token|session|auth)[^&]*/gi, "").slice(0, 160);
export const diagFromAxios = (cfg, status, extra = {}) => {
    const url = safeUrl(cfg?.url);
    const m = url.match(/\/tasks\/([^/?]+)(?:\/occurrences\/(\d{4}-\d{2}-\d{2}))?/);
    logDiag({ kind: "api", method: (cfg?.method || "?").toUpperCase(), url, status, ...(m?.[1] ? { task_id: m[1] } : {}), ...(m?.[2] ? { source_iso: m[2] } : {}), ...extra });
};

if (typeof window !== "undefined") {
    window.addEventListener("error", (e) => logDiag({ kind: "error", message: String(e?.message || e).slice(0, 200) }));
    window.addEventListener("unhandledrejection", (e) => logDiag({ kind: "unhandledrejection", message: String(e?.reason?.message || e?.reason || "").slice(0, 200) }));
}

const isoOffset = (n, from = new Date()) => { const d = new Date(from); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const bundleHash = () => ([...document.scripts].map((s) => s.src).find((src) => /\/static\/js\/main\.[a-f0-9]+\.js/.test(src)) || "").match(/main\.([a-f0-9]+)\.js/)?.[1] || "unknown";
const isPwa = () => window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

/** One row per obligation of a recurring task landing on `iso`, with SHOULD SHOW / HIDE + reason. */
export const obligationRows = (task, iso, now = new Date()) => {
    const rows = [];
    const completions = task.completions || [];
    for (const occ of getOccurrencesOnIso(task, iso)) {
        const done = completions.includes(occ.key);
        const until = task.occurrence_snoozes?.[occ.key];
        const snoozed = isOccurrenceSnoozed(task, occ.key, now.getTime());
        const [hh, mm] = (occ.time || "00:00").split(":").map(Number);
        const due = new Date(iso.slice(0, 4), iso.slice(5, 7) - 1, iso.slice(8, 10), hh, mm);
        const state = done ? "completed" : snoozed ? "snoozed" : due <= now ? "overdue/pending" : "upcoming";
        const show = !done && !snoozed;
        rows.push({ source: occ.key, display: iso, time: occ.time || task.time || null, moved: occ.movedFrom ? `moved from ${occ.movedFrom}` : "natural",
            completed: done, completion_time: task.completion_times?.[occ.key] ?? null, snoozed, snooze_until: until ?? null, state,
            verdict: show ? "SHOULD SHOW" : "SHOULD HIDE", reason: done ? `completions[] contains ${occ.key}` : snoozed ? `occurrence_snoozes[${occ.key}]=${until} > now` : "open obligation" });
    }
    return rows;
};

/** WHY would this exact task_id + occurrenceIso render (or not) right now? Mirrors the filters in
 *  App.js (todayTasks / tomorrowTasks / overdueTasks) and screensaverModel.groupTasks. */
export const explainOccurrence = (task, iso, now = new Date()) => {
    if (!task) return { found: false };
    const recurring = task.type === "recurring" && !!task.interval;
    const completions = task.completions || [];
    const today = isoOffset(0, now); const tomorrow = isoOffset(1, now);
    const out = { found: true, task_id: task.id, title: task.text, iso, recurring, rev: task.rev ?? null,
        completion_exists: recurring ? completions.includes(iso) : !!task.completed,
        completion_timestamp: recurring ? (task.completion_times?.[iso] ?? null) : (task.completed_at ?? null),
        occurrence_snoozed: recurring ? isOccurrenceSnoozed(task, iso, now.getTime()) : false,
        snooze_until: task.occurrence_snoozes?.[iso] ?? task.snoozed_until ?? null,
        instance_override: recurring ? (overrideAudit(task).canonical.find((o) => o.from === iso) || null) : null,
        archived: !!task.archived };
    if (!recurring) {
        const d = task.date;
        out.classified_today = !task.completed && d === today;
        out.classified_tomorrow = !task.completed && d === tomorrow;
        out.classified_overdue = !task.completed && !!d && d < today;
        out.included_by = out.classified_today ? "App.todayTasks (one-shot date === today)" : out.classified_tomorrow ? "App.tomorrowTasks" : out.classified_overdue ? "App.overdueTasks (daysDiff < 0)" : task.completed ? "none — completed=true" : "none";
        return out;
    }
    const display = out.instance_override?.to || iso;
    out.display_iso = display;
    out.is_obligation = getOccurrencesOnIso(task, display).some((o) => o.key === iso);
    const missed = getMissedOccurrences(task, now);
    out.missed_sources = missed;
    out.classified_overdue = missed.includes(iso);
    const open = out.is_obligation && !out.completion_exists && !out.occurrence_snoozed;
    out.classified_today = open && display === today && !out.classified_overdue;
    out.classified_tomorrow = open && display === tomorrow;
    out.included_by = out.classified_overdue ? "App.overdueTasks / groupTasks.overdue via getMissedOccurrences (source key in missed list)"
        : out.classified_today ? "App.todayTasks / groupTasks.today via getOccurrencesOnIso(today) — completions[] does NOT contain this key"
        : out.classified_tomorrow ? "App.tomorrowTasks / groupTasks.tomorrow via getOccurrencesOnIso(tomorrow)"
        : out.completion_exists ? "none — completions[] contains the key" : out.occurrence_snoozed ? "none — occurrence snoozed" : !out.is_obligation ? `none — ${iso} is not an obligation landing on ${display}` : "none — not due in Today/Tomorrow/Overdue right now";
    // G: is there a DIFFERENT key for the same task rendering on the same display day?
    out.other_keys_same_day = getOccurrencesOnIso(task, display).map((o) => o.key).filter((k) => k !== iso);
    return out;
};

const fmtTask = (t, now) => {
    const a = overrideAudit(t);
    const today = todayStr(); const tomorrow = isoOffset(1);
    const stray = (t.completions || []).filter((c) => !getOccurrencesOnIso(t, a.canonical.find((o) => o.from === c)?.to || c).some((o) => o.key === c));
    const lines = [
        `- id=${t.id} · "${t.text}" · type=${t.type} · anchor=${t.date || "-"} · interval=${t.interval ?? "-"} · base_time=${t.time || "-"} · archived=${!!t.archived}`,
        `  completed=${!!t.completed} completed_at=${t.completed_at ?? null} snoozed_until=${t.snoozed_until ?? null}`,
        `  completions=${JSON.stringify(t.completions || [])}`,
        `  completion_times=${JSON.stringify(t.completion_times || {})}`,
        `  occurrence_snoozes=${JSON.stringify(t.occurrence_snoozes || {})}`,
        `  raw_overrides=${JSON.stringify(t.instance_overrides || [])}`,
        `  effective_overrides=${JSON.stringify(a.canonical)} kinds=${JSON.stringify(a.kinds)} conflicts=${JSON.stringify(a.conflicts)} ambiguities=${JSON.stringify(a.ambiguities || [])} blocked=${a.blocked}`,
        `  stray_completion_keys=${JSON.stringify(stray)}`,
    ];
    if (t.type === "recurring" && t.interval) {
        const missed = getMissedOccurrences(t, now);
        lines.push(`  missed_sources(oldest→newest)=${JSON.stringify(missed)} → Overdue: ${missed.length ? "SHOULD SHOW" : "SHOULD HIDE"}`);
        for (const [label, iso] of [["TODAY", today], ["TOMORROW", tomorrow]]) {
            const rows = obligationRows(t, iso, now);
            if (!rows.length) lines.push(`  ${label} ${iso}: no obligation lands here → SHOULD HIDE (recurrence engine)`);
            for (const r of rows) lines.push(`  ${label} ${iso}: source=${r.source} display=${r.display} time=${r.time} ${r.moved} completed=${r.completed}${r.completion_time != null ? `@${r.completion_time}` : ""} snoozed=${r.snoozed}${r.snooze_until ? `(until ${r.snooze_until})` : ""} state=${r.state} → ${r.verdict} (${r.reason})`);
        }
        if (t._occKey || t._displayIso || t._occurrenceDate) lines.push(`  ui_identity: _occKey=${t._occKey ?? null} _displayIso=${t._displayIso ?? null} _occurrenceDate=${t._occurrenceDate ?? null} _movedFrom=${t._movedFrom ?? null}`);
    }
    return lines.join("\n");
};

export const buildDebugReport = ({ tasks = [], view = {}, counts = {}, visible = [], openTask = null, diagnose = null, diagnoseError = null, user = null }) => {
    const now = new Date();
    const a = getAuthDiag();
    const recurringVisible = visible.filter((t) => t.type === "recurring" && t.interval);
    const L = [];
    L.push("=== mEYEtasks PRODUCTION DEBUG REPORT ===");
    L.push(`generated_at=${now.toISOString()}`);
    L.push("", "--- APP / BUILD ---");
    L.push(`url=${window.location.origin} route=${window.location.pathname}${window.location.search} bundle=main.${bundleHash()}.js build_stamp=${process.env.REACT_APP_BUILD_STAMP || "n/a"} mode=${isPwa() ? "installed PWA" : "browser"} ua=${navigator.userAgent.slice(0, 120)}`);
    L.push("", "--- DEVICE CLOCK ---");
    L.push(`local=${now.toString()} tz=${Intl.DateTimeFormat().resolvedOptions().timeZone} getTimezoneOffset=${now.getTimezoneOffset()} TODAY=${todayStr()} TOMORROW=${isoOffset(1)}`);
    L.push("", "--- AUTH (sanitized) ---");
    L.push(`authenticated=${a.authenticated === null ? "unknown" : a.authenticated ? "yes" : "no"} user=${user?.email ? user.email.replace(/(.).+(@.*)/, "$1***$2") : "-"} auth_source=${a.source} last_verify=${a.lastVerify ?? "-"}@${a.lastVerifyAt ?? "-"} last_401=${a.last401At ?? "none"}`);
    L.push("", "--- UI STATE ---");
    L.push(`view=${view.primary || "-"}/${view.sub || "-"} screensaver=${!!view.screensaver} today_count=${counts.today ?? "-"} tomorrow_count=${counts.tomorrow ?? "-"} overdue_count=${counts.overdue ?? "-"} tasks_in_state=${tasks.length} open_task=${openTask?.id ?? "none"}`);
    L.push(`visible: ${visible.map((t) => `${t.id}:"${t.text}"${t._occKey ? `[src ${t._occKey}]` : ""}`).join(" | ") || "-"}`);
    if (openTask) { L.push("", "=== OPEN TASK ==="); L.push(fmtTask(openTask, now)); }
    L.push("", `--- RECURRING DIAGNOSTICS (${openTask ? "open task + " : ""}${recurringVisible.length} visible recurring) ---`);
    for (const t of recurringVisible) if (!openTask || t.id !== openTask.id) L.push(fmtTask(t, now));
    L.push("", "--- BACKEND /api/tasks/diagnose (read-only) ---");
    if (diagnoseError) L.push(`DIAGNOSTIC REQUEST FAILED: ${diagnoseError}`);
    else if (diagnose) L.push(JSON.stringify(diagnose).slice(0, 12000));
    else L.push("not requested");
    const traced = getTraced();
    if (traced.length) {
        L.push("", `--- COMPLETION TRACE (${traced.length} recently completed occurrences) ---`);
        for (const tr of traced.slice().reverse()) {
            const t = tasks.find((x) => x.id === tr.task_id);
            const ex = explainOccurrence(t, tr.iso || t?.date || todayStr(), now);
            L.push(`- task=${tr.task_id} iso=${tr.iso} "${tr.title}" clicked_at=${new Date(tr.ts).toISOString()} → ${JSON.stringify(ex)}`);
        }
    }
    L.push("", `--- RECENT EVENTS (last ${events.length}) ---`);
    for (const e of events) L.push(`${e.ts} ${e.kind} ${e.method || ""} ${e.url || ""} ${e.status ?? ""} ${e.task_id ? `task=${e.task_id}` : ""} ${e.source_iso ? `source=${e.source_iso}` : ""} ${e.iso ? `iso=${e.iso}` : ""} ${e.view ? `view=${e.view}` : ""} ${e.trace_id ? `trace=${e.trace_id}` : ""} ${e.message || ""} ${e.state ? JSON.stringify(e.state) : ""}`.replace(/\s+/g, " ").trim());
    L.push("", "=== END REPORT ===");
    return L.join("\n");
};
